import React, { useEffect } from 'react';
import {
	useRightSidebarController,
	useActiveRightSidebarState,
} from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import { useIsCollectionView } from '@atlassian/jira-polaris-component-environment/src/PolarisEnvironmentProvider.tsx';
import { useIntl } from '@atlassian/jira-intl';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import {
	QUICKSTART_UI_STATUS_OPEN,
	QUICKSTART_PANEL_ID,
	QUICKSTART_UI_STATUS_DISMISSED,
} from '../services/constants.tsx';
import {
	useOpenQuickstartPanel,
	useCloseQuickstartPanelOnOtherPanelOpen,
} from '../controllers/use-open-quickstart-panel.tsx';

import { useActions, useVisibility } from '../services/index.tsx';

import { allTasks } from '../tasks/allTasks.tsx';
import { OnboardingCheckList } from './OnboardingCheckList.tsx';

export const useQuickstartPanel = (): void => {
	const { closeRightSidebar } = useRightSidebarController();
	const { panelId } = useActiveRightSidebarState() || {};
	const openQuickstartPanel = useOpenQuickstartPanel();
	const { refreshAppState } = useActions();
	const isCollectionView = useIsCollectionView();
	const panelVisibility = useVisibility();

	// Close quickstart panel when other sidebar panels are opened
	useCloseQuickstartPanelOnOtherPanelOpen();

	useEffect(() => {
		if (isCollectionView || panelVisibility !== QUICKSTART_UI_STATUS_OPEN) {
			return;
		}

		// Only open quickstart panel if no other panel is currently active or being opened
		// This prevents quickstart from reopening when user clicks to open another panel
		if (!panelId || panelId === QUICKSTART_PANEL_ID) {
			openQuickstartPanel();
		}

		return () => {
			closeRightSidebar(QUICKSTART_PANEL_ID);
		};
	}, [
		refreshAppState,
		closeRightSidebar,
		isCollectionView,
		panelVisibility,
		openQuickstartPanel,
		panelId,
	]);
};

type TaskDefinition = { id: string; title: string; children: JSX.Element };

const QuickstartPanelContent = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { closeRightSidebar } = useRightSidebarController();
	const { closeCurrentTask, setQuickstartBannerVisibility } = useActions();

	const handleError = () => {
		closeCurrentTask();
		setQuickstartBannerVisibility({ visibility: QUICKSTART_UI_STATUS_DISMISSED });
		closeRightSidebar(QUICKSTART_PANEL_ID);
	};

	const taskDictionary = allTasks.reduce((acc: Record<string, TaskDefinition>, task) => {
		acc[task.id] = {
			id: task.id,
			title: formatMessage(task.title),
			children: task.description,
		};
		return acc;
	}, {});

	return (
		<ErrorBoundary
			id="quickstart-panel"
			packageName="atlassian/jira-polaris-component-quickstart"
			render={() => {
				handleError();
				return null;
			}}
		>
			<OnboardingCheckList taskDictionary={taskDictionary} />
		</ErrorBoundary>
	);
};

export const QuickstartPanel = (): React.JSX.Element | null => {
	const { panelId } = useActiveRightSidebarState() || {};
	const panelVisibility = useVisibility();

	// Don't render if:
	// 1. Panel is dismissed
	// 2. Another panel is active (not quickstart)
	if (
		panelVisibility === QUICKSTART_UI_STATUS_DISMISSED ||
		(panelId && panelId !== QUICKSTART_PANEL_ID)
	) {
		return null;
	}

	return <QuickstartPanelContent />;
};
